/* global rwgps, Routes */
import React, { useState, useEffect, useRef, useReducer } from 'react'
import { connect } from 'react-redux'
import styles from './Planner.scss'
import { CoreContext } from 'bundles/core/CoreContext'
import { PlannerConfirmModal, PlannerAlertModal } from '../PlannerModals'
import { reducer, initialState, actions } from './reducer'
import { useKeyboardShortcut } from './useKeyboardShortcut'
import { Settings } from '../Settings'
import { LegacyPlannerEngine } from '../engines/LegacyPlannerEngine'
import { registerModalComponent } from 'bundles/recumbent/registrationFns'
import { GlobalNav } from 'common/GlobalNav'
import { OverlayPicker } from 'common/MapV2/controls/OverlayPicker'
import { BikeLayerToggle } from 'common/MapV2/controls/BikeLayerToggle'
import { HeatLayerToggle, HEATMAP_OVERLAY_PREF } from 'common/MapV2/controls/HeatLayerToggle'
import { LoginModalOverlay } from 'common/styleguide/LoginModalOverlay'
import { SAVE_ROUTE_MODAL } from 'features/recumbent/SaveRoute'
import { LeftSidebar, RightSidebar, ContextMenu } from 'features/recumbent/PlannerSidebar'
import { CarouselModal } from 'features/recumbent/UpgradeModal'
import { RouteSplitter } from 'features/recumbent/RouteSplitter'
import { PlannerOnboardingModal } from 'features/recumbent/PlannerOnboardingModal'
import { ModalPaymentForm } from 'features/payments/PaymentForm'
import { CueReviewTool } from 'features/cue_review'
import { showModal } from 'lib/modalUtils'
import { tileServices } from 'lib/tileServices'
import * as currenUserActions from 'lib/api/currentUser'
import { base } from 'lib/styleguide/base.scss'
import { getPreferences } from 'lib/utils'
import cx from 'classnames'
import { useForceUpdate } from 'features/recumbent/PlannerSidebar/useForceUpdate'
import queryString from 'query-string'

const hasHeatmapPrivilege = window.rwgps?.config.privileges.advanced_planning // eslint-disable-line

const getPlannerEngine = () => LegacyPlannerEngine // in future, plug mapbox driven thingy in here

export const _Planner = ({ currentUser, setPreference, location }) => {
  const engineRef = useRef()
  const preferences = getPreferences()
  const query = queryString.parse(location.search)
  const [overlay, setOverlay] = useState(tileServices[query.overlay || rwgps.planner.plannerOpts.overlay] ?? tileServices.rwgps)
  const [heatLayer, setHeatLayer] = useState(hasHeatmapPrivilege && (preferences[HEATMAP_OVERLAY_PREF] ?? true))
  const [bikeLayer, setBikeLayer] = useState(false)
  const [state, dispatch] = useReducer(reducer, initialState)
  const reducerState = { plannerState: state, plannerDispatch: dispatch }

  const showSurfaceDebug = query.surfaceDebug

  // TODO do this better
  useEffect(() => Routes.sampleGraph?.resize(), [state.leftCollapse, state.rightCollapse])

  useEffect(() => {
    // mini-recumbent
    registerModalComponent('loginModalOverlay', LoginModalOverlay) // TODO next
    registerModalComponent('plannerContextMenu', ContextMenu) // blocked on rwgps.onRightClick called in map.js
    registerModalComponent('paymentForm', ModalPaymentForm) // used in rails carouselUpgradeModal invocation
    registerModalComponent('carouselUpgradeModal', CarouselModal) // used in LegacyPlannerEngine mappings and ContextMenu rightClickActions
    registerModalComponent('plannerConfirmModal', PlannerConfirmModal) // rails customConfirm
    registerModalComponent('plannerAlertModal', PlannerAlertModal) // rails customAlert
  }, [])

  // semi-hacky way to prevent scoll bounce behavior on route planner,
  // remove it before we navigate away to the rest of the app
  useEffect(() => {
    document.getElementsByTagName('body')[0].style.overflow = 'hidden'
    return () => { document.getElementsByTagName('body')[0].style.overflow = 'visible' }
  }, [])

  // FIXME i don't like that this is here. my impulse is to put it in the
  // planner reducer, but we don't have access to the engineRef in there.
  // could also go in the engine itself, potentially
  useEffect(() => {
    state.showSave && showModal(SAVE_ROUTE_MODAL, engineRef.current.saveRouteProps({ dispatch }))
  }, [state.showSave])

  const onPickOverlay = overlay => setOverlay(overlay)

  const onBikeLayerToggle = () => setBikeLayer(!bikeLayer)

  const onHeatLayerToggle = () => {
    setPreference(HEATMAP_OVERLAY_PREF, !heatLayer)
    setHeatLayer(!heatLayer)
  }

  const Engine = getPlannerEngine()
  const forceUpdate = useForceUpdate()

  const dispatchHotkeyAction = action => dispatch(actions[action])
  const dispatchClickAction = action => dispatch(actions.setClickActionCommand(action))
  const dispatchMapSetting = settings => dispatch(actions.setMapSettings(settings))
  const mod = window.navigator.platform.match(/Mac/) ? 'Meta' : 'Control'
  const undo = () => engineRef.current && engineRef.current.hotkeys().undo()
  const redo = () => engineRef.current && engineRef.current.hotkeys().redo()
  const clearMap = () => engineRef.current && engineRef.current.hotkeys().clearMap()
  const save = () => dispatch(currentUser.id ? actions.setShowSave(true) : actions.setShowSignup(true))

  useKeyboardShortcut({
    hotkeys: [
      { key: '[', fn: () => dispatchHotkeyAction('toggleLeft') },
      { key: ']', fn: () => dispatchHotkeyAction('toggleRight') },
      { key: '\\', fn: () => dispatch(actions.setSgCollapse(!state.sgCollapse)) },
      { key: '-', fn: () => engineRef.current && engineRef.current.hotkeys().zoomOut() },
      { key: '=', fn: () => engineRef.current && engineRef.current.hotkeys().zoomIn() },
      { key: 'n', fn: () => engineRef.current && engineRef.current.hotkeys().addRoute() },
      { key: 'o', fn: () => engineRef.current && engineRef.current.hotkeys().toggleZoom() },
      { key: 'u', fn: undo },
      { key: 'r', fn: redo },
      { key: 's', fn: save },
      { key: 'x', fn: clearMap },
      { key: 'Escape', fn: () => engineRef.current && engineRef.current.hotkeys().closeStreetView() }
    ],
    shortcuts: [
      { key: `${mod}+s`, fn: save },
      { key: `${mod}+z`, fn: undo },
      { key: `${mod}+y,${mod}+Shift+Z`, fn: redo },
      { key: `${mod}+Delete`, fn: clearMap }
    ],
    macros: [
      { key: 'm f', fn: () => dispatchClickAction('followRoads') },
      { key: 'm d', fn: () => dispatchClickAction('drawLines') },
      { key: 'm i', fn: () => dispatchClickAction('createPoi') },
      { key: 'm c', fn: () => dispatchClickAction('addCue') },
      { key: 'm p', fn: () => dispatchClickAction('createControlPoint') },
      { key: 't c',
        fn: () => {
          const nextCp = !state.mapSettings.showCueIcons
          setPreference('show_coursepoint_icons', nextCp, true)
          dispatchMapSetting({ ...state.mapSettings, showCueIcons: nextCp })
        }
      },
      { key: 't d',
        fn: () => {
          const nextMarkers = !state.mapSettings.showDistanceMarkers
          setPreference('route_viewer_enable_distance_markers', nextMarkers, true)
          dispatchMapSetting({ ...state.mapSettings, showDistanceMarkers: nextMarkers })
        }
      }
    ]
  })

  return (
    <CoreContext.Provider value={false}>
      <div className={cx(styles.plannerLayout, base)} tabIndex='0' id='newPlanner'>
        <GlobalNav />
        <PlannerOnboardingModal />
        <div id='route_container' className='newRP'>
          <>
            {state.showCueReview && <CueReviewTool
              cuesUuid={state.cueReviewKey}
              leftStyle={cx(styles.leftSidebar, state.leftCollapse && styles.hidden)}
              rightStyle={cx(styles.rightSidebar, state.leftCollapse && styles.hidden)}
              {...engineRef.current?.cueReviewProps({ dispatch })}
              showCueIconsWas={state.mapSettings.showCueIcons}
              forceUpdate={forceUpdate}
            />}

            <div className={cx(styles.rightSidebar, (state.rightCollapse || state.showCueReview) && styles.hidden)}>
              {state.showRouteSplitter && <RouteSplitter
                {...engineRef.current?.routeSplitterProps({ dispatch })}
              />}
              {!state.showRouteSplitter && <RightSidebar
                {...reducerState}
                {...engineRef.current?.rightSidebarProps({ dispatch, userPrefs: preferences, setPreference })}
              />}
            </div>

            <div className={cx(styles.leftSidebar, (state.leftCollapse || state.showCueReview) && styles.hidden)}>
              <LeftSidebar
                {...reducerState}
                {...engineRef.current?.leftSidebarProps({ dispatch })}
                showSurfaceDebug={showSurfaceDebug}
              />
            </div>

            <div className={cx(state.rightCollapse ? styles.expand : styles.collapse, styles.right)} onClick={() => dispatch(actions.toggleRight)} />
            <div className={cx(state.leftCollapse ? styles.expand : styles.collapse, styles.left)} onClick={() => dispatch(actions.toggleLeft)} />
          </>

          <div className={cx(styles.mapContainer, state.leftCollapse && styles.leftCollapse, state.rightCollapse && styles.rightCollapse)}>
            <OverlayPicker overlay={overlay} onPickOverlay={onPickOverlay} />
            <Settings mapSettings={state.mapSettings} dispatch={dispatch} setMapSettings={actions.setMapSettings} />
            <HeatLayerToggle heatLayer={heatLayer} toggle={onHeatLayerToggle} enabled={hasHeatmapPrivilege} />
            {overlay.type === 'google' && overlay.id !== 'satellite' && <BikeLayerToggle bikeLayer={bikeLayer} toggle={onBikeLayerToggle} /> }
            <Engine
              {...reducerState}
              ref={engineRef}
              forceUpdate={forceUpdate}
              preferences={preferences}
              mapSettings={state.mapSettings}
              heatLayer={heatLayer}
              bikeLayer={bikeLayer}
              overlay={overlay}
              showSurfaceDebug={showSurfaceDebug}
            />
          </div>
        </div>
      </div>
    </CoreContext.Provider>
  )
}

const mapStateToProps = state => ({
  currentUser: state.currentUser
})

const mapDispatchToProps = dispatch => ({
  setPreference: (key, value) => dispatch(currenUserActions.setPreference(key, value))
})

export const Planner = connect(
  mapStateToProps,
  mapDispatchToProps
)(_Planner)
